import { MenuNavItemValue } from '@components'

import { ButtonDropdown, NavItem, useModal } from '@billy/design-system'
import React, { ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { ModalId } from '../../../../../../enums/modalId'
import { useSegment } from '../../../../../../hooks'
import { useOrganizationViewUrl } from '../../../../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../../../../utils/routing/reactRoute'
import { useContactType } from '../../contexts/contactTypeContext'
import { ContactsExportDrawer } from '../ContactsExportDrawer'

enum ContactsActionId {
  Import = 'import',
  Export = 'export',
}

export const ContactsActions = (): ReactElement => {
  const [isExportDrawerOpen, setIsExportDrawerOpen] = useState(false)
  const { t } = useTranslation()
  const { track } = useSegment()
  const { url: exportsUrl } = useOrganizationViewUrl(reactRoute.organization.exports.getMainRoute())
  const navigate = useNavigate()
  const { isCustomer } = useContactType()
  const { open: openImportModal } = useModal(ModalId.ContactsImportModal)

  const moreButtonItems: NavItem<MenuNavItemValue>[] = useMemo(
    () => [
      {
        id: ContactsActionId.Export,
        children: t('contacts.header.actions.export_contacts'),
        value: exportsUrl,
      },
      {
        id: ContactsActionId.Import,
        children: t('contacts.header.actions.import_contacts'),
        value: undefined,
      },
    ],
    [exportsUrl, t],
  )

  const handleMoreSelect = useCallback(
    (id: string, value: MenuNavItemValue) => {
      if (isCustomer) {
        track('XXX - Invoicing Success - Customers List - More menu option clicked', { option: id })
      }

      if (id === ContactsActionId.Import) {
        openImportModal()
        return
      }

      if (id === ContactsActionId.Export && isCustomer) {
        setIsExportDrawerOpen(true)
        return
      }

      if (!value) {
        return
      }

      navigate(value)
    },
    [isCustomer, navigate, openImportModal, track],
  )

  const handleExportDrawerClose = useCallback(() => {
    setIsExportDrawerOpen(false)
  }, [])

  return (
    <>
      <ButtonDropdown<MenuNavItemValue> items={moreButtonItems} onSelect={handleMoreSelect} variant="outlined">
        {t('more')}
      </ButtonDropdown>
      {isCustomer && <ContactsExportDrawer isOpen={isExportDrawerOpen} onClose={handleExportDrawerClose} />}
    </>
  )
}
